import { GA4_MEASUREMENT_ID } from './const';
import { CONSENT_EVENT, readConsent } from './lib/consent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    /** Hotjar session recorder. Always undefined — Hotjar is not in use. */
    hj?: (...args: unknown[]) => void;
  }
}

/**
 * GA4 Consent Mode v2 defaults. Fired BEFORE any other gtag call so that
 * no analytics cookies are set until the user grants consent. We also
 * inject the gtag.js script unconditionally (rather than gating on
 * consent) so that we can measure consent-acceptance rate in DebugView.
 *
 * `wait_for_update: 500` gives consent a 500ms window to update before
 * `config` fires — keeps things race-safe on slow first paints.
 */
const CONSENT_MODE_DEFAULTS = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  wait_for_update: 500,
} as const;

let dataLayerReady = false;
let gtagInjected = false;
let gtagConfigured = false;

function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}

function pushConsentUpdate(): void {
  const c = readConsent();
  window.gtag?.('consent', 'update', {
    analytics_storage: c?.analytics ? 'granted' : 'denied',
    ad_storage: c?.marketing ? 'granted' : 'denied',
    ad_user_data: c?.analytics ? 'granted' : 'denied',
    ad_personalization: c?.marketing ? 'granted' : 'denied',
  });
}

function initDataLayer(): void {
  if (typeof window === 'undefined' || dataLayerReady) return;
  window.dataLayer = window.dataLayer || [];
  // Must push `arguments`, not a rest-param Array. gtag.js replays the
  // pre-load queue and only treats Arguments objects as commands — Arrays
  // sit in dataLayer but never become config/consent/events, so Realtime
  // stays empty and no _ga cookie is set.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('consent', 'default', CONSENT_MODE_DEFAULTS);
  window.gtag('js', new Date());
  dataLayerReady = true;
}

function injectGtagScript(): void {
  if (gtagInjected || !GA4_MEASUREMENT_ID) return;
  if (typeof document === 'undefined') return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);
  gtagInjected = true;
}

/**
 * `gtag('config')` must run once per page load, and only after analytics
 * consent. Every later `track*()` call goes through here too; re-configuring
 * with `send_page_view: true` would duplicate page_view hits.
 */
function configGtag(): void {
  if (gtagConfigured || !GA4_MEASUREMENT_ID || !hasAnalyticsConsent()) return;
  window.gtag('config', GA4_MEASUREMENT_ID, {
    debug_mode: import.meta.env.DEV,
    send_page_view: true,
  });
  gtagConfigured = true;
}

function ensureGtagLoaded(): void {
  if (typeof window === 'undefined' || !GA4_MEASUREMENT_ID) return;
  initDataLayer();
  injectGtagScript();
  // Consent update MUST precede config. gtag does not re-fire page_view
  // after a later grant, so config-while-denied never shows in Realtime.
  pushConsentUpdate();
  if (hasAnalyticsConsent()) {
    configGtag();
  }
}

function sendGaEvent(name: string, params?: Record<string, unknown>): void {
  ensureGtagLoaded();
  if (!hasAnalyticsConsent()) return;
  window.gtag?.('event', name, params);
}

/**
 * Hotjar is not currently in use. This is a no-op stub kept exported so
 * existing call sites in Quote.tsx (trackHjEvent("quote_complete")) and
 * trackPage404 (trackHjEvent("page_404")) continue to compile. The day
 * Hotjar is enabled, restore the body from git history and re-add the
 * HOTJAR_ID import + the `hj?: ...` Window field.
 */
export function initHotjar(): void {
  // intentionally empty
}

function onConsentChange(): void {
  // Do not reset gtagInjected / gtagConfigured — re-injecting gtag.js or
  // re-running config would duplicate the script and page_view. Consent Mode
  // `update` is enough to grant or revoke storage; config stays a one-shot.
  ensureGtagLoaded();
  initHotjar();
}

if (typeof window !== 'undefined') {
  // Initialise the dataLayer + consent defaults ASAP, even before consent,
  // so we can measure the consent-acceptance funnel itself.
  initDataLayer();
  injectGtagScript();

  window.addEventListener(CONSENT_EVENT, onConsentChange);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ensureGtagLoaded();
      initHotjar();
    });
  } else {
    ensureGtagLoaded();
    initHotjar();
  }
}

/**
 * UK postcode regex per gov.uk. The outcode (group 1) is:
 * [A-Z]{1,2}[0-9][A-Z0-9]? — area letters + 1–2 digits + optional letter.
 * The inward code is always `[0-9][A-Z]{2}`.
 *
 * For ambiguous inputs like "BN11AA" (which could be parsed as BN1+1AA
 * or BN11+AA), we accept the first structurally-valid split. BN11+AA
 * is rejected because AA isn't a valid inward (missing leading digit),
 * so BN1+1AA wins. This is a defensive trade-off: we prefer returning
 * a coarse-but-correct outcode over dropping the data point. The
 * aggregator only sees the outcode, never the inward, so re-identification
 * risk is the same as for any other outcode.
 */
const UK_OUTCODE_RE = /^[A-Z]{1,2}[0-9][0-9A-Z]?$/;
const UK_INWARD_RE = /^[0-9][A-Z]{2}$/;

/**
 * Postcode scrubbing — UK GDPR. We never send a full postcode to GA4;
 * outcode (e.g. "BN1") is the coarsest level that's still useful for
 * geographic analysis without becoming personally identifying. UK ICO
 * guidance treats outcode + age + sex as not identifying on its own
 * when used as event-level analytics, but we coarsen further to area
 * (e.g. "BN") if needed — see ANALYTICS.md.
 */
export function scrubPostcode(p: string | null | undefined): string | undefined {
  if (!p) return undefined;
  const cleaned = p.toUpperCase().replace(/\s+/g, '').trim();
  if (cleaned.length < 2 || cleaned.length > 7) return undefined;

  // Bare outcode (no inward) — accept directly.
  if (cleaned.length <= 4) {
    return UK_OUTCODE_RE.test(cleaned) ? cleaned : undefined;
  }

  // Full postcode. Walk outcode length from longest to shortest,
  // returning the first split where the prefix is a valid outcode
  // and the 3-char suffix is a valid inward. Inward is always 3 chars.
  const inwardLen = 3;
  for (let outcodeLen = cleaned.length - inwardLen; outcodeLen >= 2; outcodeLen--) {
    const outcode = cleaned.slice(0, outcodeLen);
    const inward = cleaned.slice(outcodeLen, outcodeLen + inwardLen);
    if (UK_OUTCODE_RE.test(outcode) && UK_INWARD_RE.test(inward)) {
      return outcode;
    }
  }
  return undefined;
}

/** Map 0–100 score to A–G band (UK school-style, used in dashboards). */
export function scoreBand(overall: number): 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' {
  if (overall >= 85) return 'A';
  if (overall >= 70) return 'B';
  if (overall >= 55) return 'C';
  if (overall >= 40) return 'D';
  if (overall >= 25) return 'E';
  if (overall >= 10) return 'F';
  return 'G';
}

export type QuoteStepName =
  | 'postcode'
  | 'roof_teaser'
  | 'bill'
  | 'usage'
  | 'address'
  | 'analysis'
  | 'system'
  | 'finance'
  | 'your_plan'
  | 'account'
  | 'processing'
  | 'results';

export const trackCtaClick = (name: string): void => {
  sendGaEvent('cta_click', { cta_name: name });
};

export const trackQuoteStart = (postcode?: string): void => {
  sendGaEvent('quote_start', { postcode_area: scrubPostcode(postcode) });
};

export const trackQuoteStep = (step: QuoteStepName): void => {
  sendGaEvent('quote_step', { step });
};

export const trackQuoteComplete = (quoteId: string): void => {
  sendGaEvent('quote_complete', { quote_id: quoteId });
};

export const trackQuoteAbandon = (step: QuoteStepName): void => {
  sendGaEvent('quote_abandon', { step });
};

export const trackAddressLookupSearch = (postcode: string): void => {
  sendGaEvent('address_lookup_search', { postcode_area: scrubPostcode(postcode) });
};

export const trackAddressLookupSelect = (postcode: string): void => {
  sendGaEvent('address_lookup_select', { postcode_area: scrubPostcode(postcode) });
};

export const trackAddressLookupNotFound = (postcode: string): void => {
  sendGaEvent('address_lookup_not_found', { postcode_area: scrubPostcode(postcode) });
};

export interface HelioScoreCompleteParams {
  scoreBand: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
  overallScore: number;
  helioConfidence: number | null;
  postcodeArea?: string;
  pillarCount: number;
  scoredPillarCount: number;
  stage: 'preliminary' | 'personalised' | 'verified' | 'connected';
  methodologyVersion: string;
}

export const trackHelioScoreComplete = (params: HelioScoreCompleteParams): void => {
  sendGaEvent('helio_score_complete', {
    score_band: params.scoreBand,
    overall_score: params.overallScore,
    helio_confidence: params.helioConfidence,
    postcode_area: params.postcodeArea,
    pillar_count: params.pillarCount,
    scored_pillar_count: params.scoredPillarCount,
    stage: params.stage,
    methodology_version: params.methodologyVersion,
  });
};

export interface HelioScoreLeadSubmitParams {
  scoreBand: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
  leadSource: 'quote_funnel' | 'score_widget' | 'embed';
  quoteId?: string;
  postcodeArea?: string;
}

export const trackHelioScoreLeadSubmit = (params: HelioScoreLeadSubmitParams): void => {
  sendGaEvent('helio_score_lead_submit', {
    score_band: params.scoreBand,
    lead_source: params.leadSource,
    quote_id: params.quoteId,
    postcode_area: params.postcodeArea,
  });
};

export const trackHjEvent = (name: string, _props?: Record<string, unknown>): void => {
  initHotjar();
  window.hj?.('event', name);
};

export const trackPage404 = (path: string): void => {
  trackHjEvent('page_404');
  sendGaEvent('page_404', { path });
};

export const trackSurveyStart = (projectId: string, status?: string): void => {
  sendGaEvent('survey_start', { project_id: projectId, status });
};

export const trackSurveySection = (projectId: string, section: string): void => {
  sendGaEvent('survey_section', { project_id: projectId, section });
};

export const trackSurveySave = (projectId: string, section: string, idle?: boolean): void => {
  sendGaEvent('survey_save', { project_id: projectId, section, idle: idle ? 1 : 0 });
};

export const trackSurveyPhoto = (projectId: string, photoType: string, capturePath: string): void => {
  sendGaEvent('survey_photo', { project_id: projectId, photo_type: photoType, capture_path: capturePath });
};

export const trackSurveySubmit = (projectId: string): void => {
  sendGaEvent('survey_submit', { project_id: projectId });
};
